import { useEffect, useState } from "react";
import {
  fetchPublishTargets,
  preparePublish,
  projectExportUrl,
  publishUpload,
  type PublishResult,
  type PublishSubmission,
  type PublishTarget,
} from "../api";
import { Icon } from "./Icon";

const MODE_LABEL: Record<string, string> = {
  auto: "Upload API",
  prepare: "Fiche prête · upload manuel",
  none: "Indisponible",
};

/**
 * Marketplace publishing panel. Honest by design: only `auto` targets can be uploaded by
 * API (per-account, confirm-gated, once credentials are wired); `prepare` targets build the
 * fiche for a manual upload. Running a "prepare" validates the project's fiche against the
 * chosen site's rules (formats, size, required fields) -- no network, no upload.
 */
export function PublishPanel({ projectId }: { projectId: string }) {
  const [targets, setTargets] = useState<PublishTarget[]>([]);
  const [sel, setSel] = useState<string>("");
  const [sub, setSub] = useState<PublishSubmission | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>("");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<PublishResult | null>(null);

  useEffect(() => {
    fetchPublishTargets()
      .then((r) => {
        setTargets(r.targets);
        if (r.targets[0]) setSel(r.targets[0].id);
      })
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, []);

  const target = targets.find((t) => t.id === sel);

  async function doPrepare() {
    if (!sel) return;
    setBusy(true);
    setErr("");
    setSub(null);
    setResult(null);
    try {
      setSub(await preparePublish(projectId, sel));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function doUpload() {
    if (!sel) return;
    if (!window.confirm(`Publier réellement ce projet sur ${target?.name} ? Action sortante.`)) return;
    setUploading(true);
    setErr("");
    setResult(null);
    try {
      setResult(await publishUpload(projectId, sel, true));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom: 22 }}>
      <div className="row" style={{ gap: 12, alignItems: "flex-start", marginBottom: 14 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, display: "grid", placeItems: "center", color: "var(--accent-3)", background: "var(--accent-soft)", border: "1px solid var(--accent-line)" }}><Icon name="upload" size={18} /></div>
        <div>
          <div className="card-title">Publication marketplace</div>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 3, maxWidth: 620, lineHeight: 1.5 }}>
            Valide la fiche du projet pour un site donné (formats, taille, champs requis). Seuls MyMiniFactory & Thingiverse ont une API d'upload (publication par compte, après confirmation) ; les autres se publient manuellement à partir du ZIP.
          </div>
        </div>
      </div>

      <div className="row wrap" style={{ gap: 8, alignItems: "flex-end" }}>
        <div className="field" style={{ marginBottom: 0, flex: 1, minWidth: 220 }}>
          <label>Destination</label>
          <select value={sel} onChange={(e) => { setSel(e.target.value); setSub(null); setErr(""); }}>
            {targets.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} — {MODE_LABEL[t.upload_mode]}{t.paid ? " · payant" : ""}
              </option>
            ))}
          </select>
        </div>
        <button className="btn primary" disabled={busy || !sel} onClick={doPrepare}>
          {busy ? <><span className="spinner" /> Validation…</> : <><Icon name="shield" size={15} /> Valider la fiche</>}
        </button>
        <a className="btn" href={projectExportUrl(projectId)} download><Icon name="download" size={15} /> ZIP fiche</a>
      </div>

      {target && (
        <div className="muted row wrap" style={{ gap: 10, fontSize: 11.5, marginTop: 10 }}>
          <span>Formats: {target.formats.join(", ")}</span>
          <span>· Max {target.max_file_mb} Mo/fichier</span>
          <a href={target.docs} target="_blank" rel="noreferrer" style={{ color: "var(--accent-3)" }}>docs ↗</a>
        </div>
      )}
      {target && (
        <div className="muted" style={{ fontSize: 11.5, marginTop: 6, lineHeight: 1.5 }}>
          <Icon name="external" size={11} /> {target.automation_note}
        </div>
      )}

      {err && <div className="muted" style={{ color: "var(--bad)", fontSize: 12.5, marginTop: 12 }}>{err}</div>}

      {sub && (
        <div style={{ marginTop: 16, borderTop: "1px solid var(--line)", paddingTop: 14 }}>
          <div className="row wrap" style={{ gap: 10, alignItems: "center" }}>
            <span className={`badge ${sub.ready ? "ok" : "warn"}`} style={{ fontWeight: 600 }}>
              {sub.ready ? "✓ Fiche prête" : "✗ Non prête"}
            </span>
            <span className="muted" style={{ fontSize: 12 }}>
              {sub.files.filter((f) => f.ok).length}/{sub.files.length} fichier(s) publiable(s) · {sub.target_name}
            </span>
            {sub.upload_mode === "auto" && (
              <span className="muted" style={{ fontSize: 12 }}>
                {sub.credentials_present ? "· credentials OK" : `· credentials manquants (${sub.missing_env.join(", ")})`}
              </span>
            )}
          </div>

          {sub.blockers.length > 0 && (
            <ul style={{ margin: "10px 0 0", paddingLeft: 18, fontSize: 12.5, color: "var(--bad)" }}>
              {sub.blockers.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          )}
          {sub.warnings.length > 0 && (
            <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 12.5 }} className="muted">
              {sub.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}

          {sub.upload_mode === "auto" ? (
            sub.ready && sub.credentials_present ? (
              <div style={{ marginTop: 12 }}>
                <button className="btn primary" disabled={uploading} onClick={doUpload}>
                  {uploading ? <><span className="spinner" /> Publication…</> : <><Icon name="upload" size={15} /> Publier sur {sub.target_name}</>}
                </button>
                <span className="muted" style={{ fontSize: 11.5, marginLeft: 10 }}>publication réelle · confirmation demandée</span>
              </div>
            ) : (
              <div className="muted" style={{ fontSize: 12, marginTop: 12, lineHeight: 1.5 }}>
                Renseigne les credentials du site dans l'environnement ({sub.missing_env.join(", ") || "clés API"}) pour activer l'upload API.
              </div>
            )
          ) : (
            <div className="muted" style={{ fontSize: 12, marginTop: 12, lineHeight: 1.5 }}>
              Ce site n'a pas d'upload automatisé. Télécharge le <strong>ZIP fiche</strong> et publie manuellement.
            </div>
          )}

          {result && (
            <div className="badge ok" style={{ marginTop: 12, display: "block", padding: "8px 12px", fontSize: 12.5 }}>
              ✓ Publié sur {result.target}
              {result.url ? <> — <a href={String(result.url)} target="_blank" rel="noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>voir la fiche ↗</a></> : null}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
